import { Children, cloneElement, isValidElement, useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import UserExistence from "../Cloud/UserExistenceEnum";
import Loader from "../Components/Loader";
import { useMicrospecDynamicUIContext } from "../GlobalProvider";
import { getOrDefault } from "../Helpers/Utils";
import getPagesElement from "../Pages/ElementsMapper";
import { useMicrospecThemeLanguageContext } from "../Themes";
import ProfileCompletion from "./ProfileCompletion";
import VerificationRequiredPage from "./VerificationRequiredPage";
import { useOrganizationContext } from "../Dashboard/OrganizationContext";

// Helper function to render element with injected props
const renderElementWithProps = (element, user, signOut, organizationContextAware) => {
  return Children.map(element, (child) => {
    return isValidElement(child)
      ? cloneElement(child, { user, signOut })
      : child;
  });
};

const AuthRoute = ({ config, path, element, neededRoles = [], organizationContextAware }) => {
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const organizationContext = useOrganizationContext();

  const { user, userProfile, loading, signOut, completed } = cloudProvider;

  // Extract auth configuration with memoization
  const authConfig = useMemo(() => {
    const application = getOrDefault(config.application, {});
    const auth = getOrDefault(application.auth, {});
    return {
      verificationRequired: getOrDefault(auth, false).verificationRequired
    };
  }, [config]);

  // Memoize profile completion handlers
  const handleProfileComplete = useCallback(async (profileData) => {
    try {
      await userProfile.methods.updateUserProfile(profileData);
      // Force page reload to refresh user profile state
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error; // Let ProfileCompletion component handle the error display
    }
  }, [userProfile]);

  const handleBackToIdentifier = useCallback(() => {
    signOut();
  }, [signOut]);

  // Memoize role checking logic
  const hasRequiredRole = useMemo(() => {
    if (!neededRoles || neededRoles.length === 0) {
      return true;
    }

    const userRoles = userProfile?.attributes?.roles || [];
    return neededRoles.some(role => userRoles.includes(role));
  }, [neededRoles, userProfile?.attributes?.roles]);

  // Show loader while authentication state is being determined
  if (loading) {
    return <Loader msg={localizationProvider.translateByType({
      type: "Internal",
      text: "loadingInitialData"
    }) || "Loading initial data..."} />;
  }

  // Redirect to login if user is not authenticated
  if (!user) {
    const loginPath = path ? `/login?redirectTo=${path}` : "/login";
    return <Navigate to={loginPath} replace />;
  }

  // Show loader while application is initializing
  if (!completed) {
    return <Loader msg={localizationProvider.translateByType({
      type: "Internal",
      text: "loadingApplicationData"
    }) || "Loading application data..."} />;
  }

  // Show loader while user profile existence is being determined
  if (userProfile?.userExistence === UserExistence.LOADING) {
    return <Loader msg={localizationProvider.translateByType({
      type: "Internal",
      text: "loadingUserProfile"
    }) || "Loading user profile..."} />;
  }

  // Show profile completion for new users
  if (userProfile?.userExistence === UserExistence.NOT_EXISTS) {
    return (
      <ProfileCompletion
        identifier={user.phoneNumber || user.email || ''}
        countryCode=""
        loading={false}
        error={false}
        onProfileComplete={handleProfileComplete}
        onBackToIdentifier={handleBackToIdentifier}
      />
    );
  }

  // Handle unknown user existence states
  if (userProfile?.userExistence !== UserExistence.EXISTS) {
    return getPagesElement({ type: "NotFound" });
  }

  // Check email verification requirement
  if (authConfig.verificationRequired &&
    user.providerData?.[0]?.email &&
    !user.emailVerified) {
    return (
      <VerificationRequiredPage
        isVerified={user.emailVerified}
        userEmail={user.email}
      />
    );
  }
  if (
    organizationContextAware &&
    organizationContext.loading
  ) {
    return <Loader />;
  }
  // Render protected content with role-based access control
  const protectedContent = (renderElementWithProps(element, user, signOut));

  return hasRequiredRole
    ? protectedContent
    : getPagesElement({ type: "NotFound" });
};

export default AuthRoute;
